import React, { useState, useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ChevronDown } from "lucide-react";
import Button from "../../../../components/ui/Button/Button";
import { faqData } from "../../../../data/faq";
import "./FAQ.css";

gsap.registerPlugin(ScrollTrigger);

const FAQItem = ({ item, index, openIndex, toggleQuestion }) => {
  const answerRef = useRef(null);
  const isOpen = openIndex === index;

  useEffect(() => {
    const el = answerRef.current;
    if (isOpen) {
      gsap.fromTo(
        el,
        { height: 0, opacity: 0 },
        {
          height: "auto",
          opacity: 1,
          duration: 0.4,
          ease: "power3.out",
        },
      );
    } else {
      gsap.to(el, {
        height: 0,
        opacity: 0,
        duration: 0.3,
        ease: "power3.in",
      });
    }
  }, [isOpen]);

  return (
    <div className={`faq-item ${isOpen ? "faq-item--open" : ""}`}>
      <button
        className="faq-item__question"
        onClick={() => toggleQuestion(index)}
      >
        <span>{item.question}</span>
        <span className="faq-item__icon">
          <ChevronDown size={20} strokeWidth={2} />
        </span>
      </button>
      <div className="faq-item__answer" ref={answerRef}>
        <p>{item.answer}</p>
      </div>
    </div>
  );
};

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const questionsRef = useRef(null);

  const toggleQuestion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      const headerElements = headerRef.current.querySelectorAll(".animate-in");
      gsap.fromTo(
        headerElements,
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.15,
          ease: "power3.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 70%",
            toggleActions: "play none none none",
            once: true,
          },
        },
      );

      const items = questionsRef.current.querySelectorAll(".faq-item");
      gsap.fromTo(
        items,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          stagger: 0.1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: questionsRef.current,
            start: "top 75%",
            toggleActions: "play none none none",
            once: true,
          },
        },
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section className="faq" ref={sectionRef}>
      <div className="faq__container">
        <div className="faq__header" ref={headerRef}>
          <div className="faq__badge animate-in">{faqData.badge}</div>
          <h2 className="faq__headline animate-in">{faqData.headline}</h2>
          <p className="faq__description animate-in">{faqData.description}</p>
          <div className="animate-in">
            <Button
              text={faqData.ctaButton.text}
              variant="primary"
              link={faqData.ctaButton.link}
            />
          </div>
        </div>

        <div className="faq__questions" ref={questionsRef}>
          {faqData.questions.map((item, index) => (
            <FAQItem
              key={index}
              item={item}
              index={index}
              openIndex={openIndex}
              toggleQuestion={toggleQuestion}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQ;
